import React from 'react';
import type { EmailTriageMessage } from '../../../services/emailTriageApi';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const EmailTriagePage = React.lazy(() => import('../../email-triage/EmailTriagePage'));

// Invented demo data only — no real person or agency.
const hoursAgo = (h: number) => new Date(Date.now() - h * 3600_000).toISOString();

const msg = (
  id: string,
  fromAddr: string,
  subject: string,
  hours: number,
  label: EmailTriageMessage['label'],
  status: EmailTriageMessage['status'],
  priority: EmailTriageMessage['priority'],
  triageSummary: string,
  bodyText: string,
): EmailTriageMessage => ({
  id,
  agencyId: 'demo',
  accountId: 'demo',
  messageId: `<${id}@demo>`,
  fromAddr,
  toAddr: 'office@demo-pflege.at',
  subject,
  date: hoursAgo(hours),
  snippet: bodyText.slice(0, 90),
  bodyText,
  bodyHtml: null,
  label,
  status,
  priority,
  triageSummary,
  triagedAt: hoursAgo(hours),
  draftText: null,
  draftUpdatedAt: null,
  sentReplyAt: null,
  hasDraft: false,
  attachments: null,
  createdAt: hoursAgo(hours),
});

const MESSAGES: EmailTriageMessage[] = [
  msg('m1', 'Maria Berger <maria.berger@example.at>', 'Hausbesuch am Montag möglich?', 1, 'appointment', 'REPLY_NEEDED', 'URGENT',
    'Frau Berger fragt, ob der Hausbesuch für ihre Mutter am Montag um 10:00 Uhr stattfinden kann.',
    'Guten Tag,\n\nkönnten Sie am Montag um 10:00 Uhr zu meiner Mutter, Frau Josefine Huber, kommen? Wir möchten den Betreuungswechsel besprechen.\n\nVielen Dank\nMaria Berger'),
  msg('m2', 'Steuerbüro Lechner <office@lechner-steuer.example>', 'Rückfrage zur Rechnung März', 3, 'invoice', 'REVIEW', 'NORMAL',
    'Das Steuerbüro bittet um eine Kopie der Rechnung RE-2026-0142.',
    'Sehr geehrte Damen und Herren,\n\nbitte senden Sie uns eine Kopie der Rechnung RE-2026-0142 für die Buchhaltung.\n\nMit freundlichen Grüßen\nSteuerbüro Lechner'),
  msg('m3', 'Josef Huber <josef.huber@example.at>', 'Neue Betreuerin für meine Mutter', 5, 'patient', 'REPLY_NEEDED', 'NORMAL',
    'Herr Huber möchte wissen, wann die neue Betreuerin anreist.',
    'Hallo,\n\nwann kommt die neue Betreuerin an? Wir würden sie gerne am Bahnhof abholen.\n\nLG Josef Huber'),
  msg('m4', 'Hausarztpraxis Dr. Wimmer <praxis@wimmer.example>', 'Befund Frau Huber', 20, 'document', 'REVIEW', 'NORMAL',
    'Die Praxis schickt den aktuellen Befund von Frau Huber.',
    'Anbei der Befund vom letzten Kontrolltermin.'),
  msg('m5', 'Ana Popescu <ana.popescu@example.ro>', 'Anreise am 1. März bestätigt', 26, 'patient', 'DONE', 'LOW',
    'Die Betreuerin bestätigt ihre Anreise am 1. März.',
    'Guten Tag, ich komme am 1. März mit dem Bus um 14:00 Uhr in Graz an. Liebe Grüße, Ana'),
  msg('m6', 'Pflege-Newsletter <news@pflege-news.example>', 'Neuigkeiten zur 24-Stunden-Betreuung', 48, 'newsletter', 'DONE', 'LOW',
    'Allgemeiner Newsletter, keine Antwort nötig.',
    'Die neuesten Entwicklungen in der 24-Stunden-Betreuung …'),
  msg('m7', 'Karl Wagner <karl.wagner@example.at>', 'Termin für Erstgespräch', 52, 'appointment', 'WAITING', 'NORMAL',
    'Herr Wagner wartet auf die Bestätigung des Erstgesprächs.',
    'Passt Ihnen Donnerstag 15 Uhr für das Erstgespräch?'),
];

const AI_REPLY =
  'Sehr geehrte Frau Berger,\n\nvielen Dank für Ihre Nachricht. Gerne bestätigen wir den Hausbesuch bei Ihrer Mutter, Frau Josefine Huber, am Montag um 10:00 Uhr. Dabei besprechen wir in Ruhe den Betreuungswechsel.\n\nMit freundlichen Grüßen\nIhr Pflege-Team';

const ok = (data: unknown) => ({ success: true, data });

const list = (params: Record<string, string>) => {
  let messages = params.trash ? [] : MESSAGES;
  if (params.status) messages = messages.filter((m) => m.status === params.status);
  if (params.label) messages = messages.filter((m) => m.label === params.label);
  if (params.urgentOnly) messages = messages.filter((m) => m.priority === 'URGENT');
  const counts: Record<string, number> = {};
  MESSAGES.forEach((m) => { counts[m.status] = (counts[m.status] || 0) + 1; });
  return ok({ messages, counts, urgentCount: MESSAGES.filter((m) => m.priority === 'URGENT').length, trashCount: 0, visibleDays: 30 });
};

const ACCOUNT = {
  id: 'demo', agencyId: 'demo', emailAddress: 'office@demo-pflege.at', imapHost: 'imap.demo-pflege.at', imapPort: 993,
  imapSecure: true, smtpHost: 'smtp.demo-pflege.at', smtpPort: 465, smtpSecurity: 'ssl', username: 'office@demo-pflege.at',
  authType: 'password', knowledgeText: null, visibleDays: 30, isActive: true, lastSyncAt: hoursAgo(0.1),
  lastSyncError: null, createdAt: hoursAgo(500), updatedAt: hoursAgo(1),
};

const paginated = <T,>(data: T[]) => ({ data, total: data.length, page: 1, limit: 100, totalPages: 1 });
// Recipients/attachments pickers in the compose dialog — never the agency's
// real clients, caregivers or documents.
const PATIENTS = [{ id: 'p1', firstName: 'Josefine', lastName: 'Huber', email: 'maria.berger@example.at', isActive: true }];
const CAREGIVERS = [{ id: 'c1', firstName: 'Ana', lastName: 'Popescu', email: 'ana.popescu@example.ro', isActive: true }];
const notInPreview = () => {
  // Rejected like a server error so the page shows its normal error toast.
  throw Object.assign(new Error('In der Vorschau nicht verfügbar.'), {
    response: { status: 400, data: { message: 'In der Vorschau nicht verfügbar.' } },
  });
};

const mocks: DemoRoute[] = [
  { path: /^\/email-triage\/account$/, reply: () => ok(ACCOUNT) },
  // Settings tab: save returns the account (null would flip the page to the
  // setup screen); test returns a result object; the Microsoft sign-in must
  // never redirect to a real OAuth consent for the agency.
  { method: 'post', path: /^\/email-triage\/account$/, reply: () => ok(ACCOUNT) },
  { method: 'post', path: /^\/email-triage\/test-connection$/, delayMs: 800, reply: () => ok({ imapOk: true, smtpOk: true }) },
  { path: /^\/email-triage\/oauth\/microsoft\/start$/, reply: notInPreview },
  { path: /^\/email-triage\/oauth\/providers$/, reply: () => ok({ microsoft: { available: true } }) },
  { path: /^\/email-triage\/messages$/, reply: ({ params }) => list(params) },
  { path: /^\/email-triage\/messages\/([^/]+)$/, reply: ({ match }) => ok(MESSAGES.find((m) => m.id === match[1]) || MESSAGES[0]) },
  { method: 'post', path: /^\/email-triage\/ai-assist$/, delayMs: 1500, reply: ({ body }) => ok({ text: AI_REPLY, action: body?.action || 'reply', contextUsed: { incomingEmail: true, contacts: 0, draft: false } }) },
  { method: 'post', path: /^\/email-triage\/sync$/, delayMs: 1200, reply: () => ok({ fetched: 0, triaged: 0 }) },
  { path: /^\/email-triage\/sent$/, reply: () => ok({ messages: [], total: 0 }) },
  { path: /^\/email-triage\/contacts\/search$/, reply: () => ok({ contacts: [] }) },
  { path: /^\/patients$/, reply: () => paginated(PATIENTS) },
  { path: /^\/caregivers$/, reply: () => paginated(CAREGIVERS) },
  { path: /^\/templates$/, reply: () => [] },
  { path: /^\/templates\/filled$/, reply: () => paginated([]) },
  { path: /^\/files$/, reply: () => ok([]) },
];

const k = (key: string) => `featurePreview.emailTriage.${key}`;

export const emailTriageTour: FeatureTour = {
  Page: EmailTriagePage,
  mocks,
  chapters: [
    {
      title: k('ch2'),
      steps: [
        { at: 0.5, target: 'et-filters', balloon: { title: k('hs2Title'), text: k('hs2Text') } },
        { at: 5, target: 'et-list', balloon: { title: k('tourListTitle'), text: k('tourListText') } },
      ],
      duration: 10,
    },
    {
      title: k('tourOpenTitle'),
      steps: [
        { at: 0.5, target: 'et-message', action: 'click' },
        { at: 1.5, target: 'et-summary', balloon: { title: k('tourSummaryTitle'), text: k('tourSummaryText') } },
      ],
      duration: 8,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.3, target: 'et-message', action: 'click' },
        { at: 1.5, target: 'et-ai-reply', balloon: { title: k('tourAiTitle'), text: k('tourAiText') } },
        { at: 4, target: 'et-ai-reply', action: 'click' },
        { at: 6.5, target: 'et-draft', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
        { at: 11, target: 'et-send', balloon: { title: k('tourSendTitle'), text: k('tourSendText') } },
      ],
      duration: 15,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 40, aMax: 200,
    bLabel: k('savB'), b: 3,
    toHoursPerMonth: (a, b) => (a * b * 21) / 60,
  },
};
